import { List, fromJS } from 'immutable'
import React, { useEffect, useState } from 'react'
import { Link, NavLink, Route, Switch, useHistory, useParams } from 'react-router-dom'
import { api, tokenApi } from '../common/api'
import { Modal } from '../common/modal'
import Table from '../common/table'
import { useTranslator } from '../common/translator'
import ContentRoot from './content'
import './passcode.sass'
import TabCopy from './widget/TabCopy'
import TabCss from './widget/TabCss'
import TabEmbed from './widget/TabEmbed'
import TabGeneral from './widget/TabGeneral'
import TabPreview from './widget/TabPreview'
import TabSlides from './widget/TabSlides'
import TabStats from './widget/TabStats'
import TabStyling from './widget/TabStyling'
import { Wrapper } from './wrapper'

function OrgNav({ organizationId, widgetsEnabled, t }) {
  return (
    <div className="org-nav">
      <NavLink exact to={`/yritys/${organizationId}`} className="org-nav-tab" activeClassName="org-nav-tab--active">
        {t('tabContent') || 'Aineisto'}
      </NavLink>
      {widgetsEnabled && (
        <NavLink to={`/yritys/${organizationId}/widget`} className="org-nav-tab" activeClassName="org-nav-tab--active">
          {t('tabWidgets') || 'Widgetit'}
        </NavLink>
      )}
    </div>
  )
}

function NewWidgetModal({ token, onClose, onCreate, t }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const handleCreate = () => {
    if (!title.trim()) return
    setLoading(true)
    setError(false)
    tokenApi
      .post(token, '/dashboard/widget', { title, description })
      .then(res => {
        if (!res.data.success) throw Error(res.data.message || 'Error')
        onCreate(res.data.widget)
        onClose()
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }

  return (
    <Modal title={t('widget.newButton') || 'New widget'} onClose={onClose} size="small">
      <div className="widget-form">
        {error && <div className="passcode-error">{t('widget.createError') || 'An error occurred'}</div>}
        <div className="widget-form-field">
          <label>{t('widget.fieldTitle') || 'Title'}</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="widget-form-field">
          <label>{t('widget.fieldDescription') || 'Description'}</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} />
        </div>
        <div className="widget-form-actions">
          <button className="widget-new-btn" onClick={handleCreate} disabled={loading || !title.trim()}>
            {loading ? <i className="fa fa-spin fa-spinner" /> : null} {t('widget.create') || 'Create'}
          </button>
          <button className="widget-cancel-btn" onClick={onClose} disabled={loading}>
            {t('widget.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function WidgetEdit({ token, t, organizationId }) {
  const { widget_id: widgetId, tab } = useParams()
  const activeTab = tab || 'general'
  const history = useHistory()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [slides, setSlides] = useState([{ titleText: '', contentText: '', personName: '', personTitle: '', company: '', fileId: '', ctaText: '', ctaUrl: '' }])
  const [mediaGroups, setMediaGroups] = useState([])
  const [mediaLoading, setMediaLoading] = useState(true)
  const [domain, setDomain] = useState('')
  const [layout, setLayout] = useState('')
  const [accentColor, setAccentColor] = useState('#000000')
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [maxWidth, setMaxWidth] = useState('')
  const [titleFontSize, setTitleFontSize] = useState('')
  const [contentFontSize, setContentFontSize] = useState('')
  const [textColor, setTextColor] = useState('#111111')
  const [customCss, setCustomCss] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(false)
  const [saved, setSaved] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    if (!tab) history.replace(`/yritys/${organizationId}/widget/${widgetId}/general`)
  }, [widgetId])

  useEffect(() => {
    tokenApi.get(token, `/dashboard/widget/${widgetId}`).then(res => {
      if (res.data.success) {
        const w = res.data.widget
        const data = w.data || {}
        setTitle(w.title || '')
        setDescription(w.description || '')
        setDomain((Array.isArray(data.domains) ? data.domains.join(', ') : data.domain) || '')
        setLayout(data.layout || '')
        setAccentColor(data.accentColor || '#000000')
        setBackgroundColor(data.backgroundColor || '#ffffff')
        setMaxWidth(data.maxWidth ? String(data.maxWidth) : '')
        setTitleFontSize(data.titleFontSize ? String(data.titleFontSize) : '')
        setContentFontSize(data.contentFontSize ? String(data.contentFontSize) : '')
        setTextColor(w.text_color || '#111111')
        setCustomCss(w.custom_css || '')
        if (data.slides && data.slides.length > 0) {
          setSlides(data.slides)
        } else {
          setSlides([{
            titleText: w.title_text || '',
            contentText: w.content_text || '',
            personName: w.person_name || '',
            personTitle: w.person_title || '',
            company: w.company || '',
            fileId: w.file_id || '',
            ctaText: w.cta_text || '',
            ctaUrl: w.cta_url || '',
          }])
        }
      }
    }).finally(() => setLoading(false))
  }, [widgetId])

  useEffect(() => {
    tokenApi
      .get(token, '/dashboard/content')
      .then(res => {
        if (!res.data.success) return
        const groups = (res.data.content || [])
          .map(item => ({
            id: item.id,
            label: item.label,
            files: (item.materials || [])
              .map(m => m.file)
              .filter(file => file && file.id && file.meta && file.meta.type && (file.meta.type.includes('image') || file.meta.type.includes('video'))),
          }))
          .filter(group => group.files.length > 0)
        setMediaGroups(groups)
      })
      .finally(() => setMediaLoading(false))
  }, [organizationId])

  // Parsed here rather than checked raw, so a value like "," can't pass as a domain and
  // leave the widget embeddable anywhere.
  const domainList = domain.split(/[\n,]/).map(s => s.trim()).filter(Boolean)

  const handleSave = () => {
    setSaving(true)
    setError(false)
    setSaved(false)
    tokenApi
      .patch(token, `/dashboard/widget/${widgetId}`, { title, description, customCss, domains: domainList, layout, accentColor, backgroundColor, maxWidth, titleFontSize, contentFontSize, textColor, slides })
      .then(res => {
        if (!res.data.success) throw Error(res.data.message || 'Error')
        setSaved(true)
      })
      .catch(() => setError(true))
      .finally(() => setSaving(false))
  }

  const handleDelete = () => {
    setDeleting(true)
    tokenApi
      .delete(token, `/dashboard/widget/${widgetId}`)
      .then(res => {
        if (!res.data.success) throw Error(res.data.message || 'Error')
        history.push(`/yritys/${organizationId}/widget`)
      })
      .catch(() => {
        setError(true)
        setDeleting(false)
        setConfirmDelete(false)
      })
  }

  const tabs = [
    { key: 'general', label: t('widget.tabGeneral') || 'Perustiedot' },
    { key: 'slides', label: t('widget.tabSlides') || 'Sisältö' },
    { key: 'styling', label: t('widget.tabStyling') || 'Tyylit' },
    { key: 'css', label: t('widget.tabCss') || 'CSS' },
    { key: 'embed', label: t('widget.tabEmbed') || 'Upotus' },
    { key: 'stats', label: t('widget.tabStats') || 'Tilastot' },
    // Internal tool for moving a widget between environments. Hidden from the tab bar for
    // now — reachable by putting /copy in the URL.
    { key: 'copy', label: 'Kopiointi', hidden: true },
  ]

  const mediaFiles = mediaGroups.flatMap(group => group.files)

  const tabProps = { token, title, setTitle, description, setDescription, domain, setDomain, domainList, slides, setSlides, mediaGroups, mediaFiles, mediaLoading, layout, setLayout, textColor, setTextColor, accentColor, setAccentColor, backgroundColor, setBackgroundColor, maxWidth, setMaxWidth, titleFontSize, setTitleFontSize, contentFontSize, setContentFontSize, customCss, setCustomCss, widgetId, t }

  return (
    <div className="page-view">
      <div className="back-button">
        <Link className="back-button-item" to={`/yritys/${organizationId}/widget`}>
          {t('back') || 'Back'}
        </Link>
      </div>
      {loading ? (
        <div className="loader loader--box">
          <i className="fas fa-spin fa-spinner" />
        </div>
      ) : (
        <React.Fragment>
          <button className="widget-preview-toggle" onClick={() => setPreviewOpen(open => !open)}>
            {previewOpen
              ? (t('widget.hidePreview') || 'Hide preview')
              : (t('widget.showPreview') || 'Show preview')}
          </button>
          {previewOpen && (
            <div className="widget-preview-pinned">
              <TabPreview {...tabProps} />
            </div>
          )}
          <div className="widget-tabs">
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={`widget-tab${activeTab === tab.key ? ' widget-tab--active' : ''}`}
                style={tab.hidden ? { display: 'none' } : undefined}
                onClick={() => history.push(`/yritys/${organizationId}/widget/${widgetId}/${tab.key}`)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="widget-form">
            {error && <div className="passcode-error">{t('widget.saveError') || 'An error occurred'}</div>}
            {saved && <div className="widget-saved">{t('widget.saveSuccess') || 'Saved!'}</div>}
            {activeTab === 'general' && <TabGeneral {...tabProps} />}
            {activeTab === 'slides' && <TabSlides {...tabProps} />}
            {activeTab === 'styling' && <TabStyling {...tabProps} />}
            {activeTab === 'css' && <TabCss {...tabProps} />}
            {activeTab === 'embed' && <TabEmbed {...tabProps} />}
            {activeTab === 'stats' && <TabStats {...tabProps} />}
            {activeTab === 'copy' && <TabCopy {...tabProps} />}
            <div className="widget-form-actions">
              <button className="widget-new-btn" onClick={handleSave} disabled={saving || deleting || !title.trim() || domainList.length === 0}>
                {saving ? <i className="fa fa-spin fa-spinner" /> : null} {t('widget.save') || 'Save'}
              </button>
              {confirmDelete ? (
                <React.Fragment>
                  <button className="widget-delete-btn" onClick={handleDelete} disabled={deleting}>
                    {deleting ? <i className="fa fa-spin fa-spinner" /> : null} {t('widget.deleteConfirm') || 'Confirm delete'}
                  </button>
                  <button className="widget-cancel-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    {t('widget.cancel') || 'Cancel'}
                  </button>
                </React.Fragment>
              ) : (
                <button className="widget-delete-btn" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  {t('widget.delete') || 'Delete'}
                </button>
              )}
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

function WidgetList({ token, organizationId, t }) {
  const history = useHistory()
  const [modalOpen, setModalOpen] = useState(false)
  const [widgets, setWidgets] = useState(List())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    tokenApi.get(token, '/dashboard/widget').then(res => {
      if (res.data.success) setWidgets(fromJS(res.data.widgets))
    }).finally(() => setLoading(false))
  }, [])

  const handleCreate = widget => {
    history.push(`/yritys/${organizationId}/widget/${widget.id}`)
  }
  const columns = [
    {
      key: 'title',
      label: t('widget.tableName'),
      render: item => (
        <Link to={`/yritys/${organizationId}/widget/${item.get('id')}`}>{item.get('title')}</Link>
      ),
    },
    {
      key: 'created_at',
      label: t('widget.tableDate'),
      width: 160,
      align: 'center',
      date: 'L',
    }
  ]

  return (
    <div className="page-view">
      <div className="page-view-actions">
        <button className="widget-new-btn" onClick={() => setModalOpen(true)}>
          <i className="fa fa-plus" /> {t('widget.newButton') || 'New widget'}
        </button>
      </div>
      {loading ? (
        <div className="loader loader--box">
          <i className="fas fa-spin fa-spinner" />
        </div>
      ) : (
        <Table rows={widgets} columns={columns} id="id" />
      )}
      {modalOpen && <NewWidgetModal token={token} onClose={() => setModalOpen(false)} onCreate={handleCreate} t={t} />}
    </div>
  )
}

function PasscodeForm({ organizationId, setToken, t }) {
  const [value, setValue] = useState('')
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const onSubmit = event => {
    event.preventDefault()
    setLoading(true)

    api
      .post('/dashboard/token', {
        organization_id: organizationId,
        passcode: value,
      })
      .then(res => {
        const token = res.data.token
        if (!token) throw Error('Pääsykoodi virheellinen')
        localStorage[`token_${organizationId}`] = token
        setToken(token)
      })
      .catch(err => {
        console.log(err)
        setError(true)
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className="passcode-page">
      <div className="passcode-form">
        <form onSubmit={onSubmit}>
          {error && <div className="passcode-error">{t('loginError')}</div>}
          <h3>{t('loginTitle')}</h3>
          <div className="passcode-form-input">
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={t('loginPlaceholder')}
            />
          </div>
          <div className="passcode-form-submit">
            <button>{t('loginButton')}</button>
          </div>
        </form>
        {loading && (
          <div className="passcode-loader">
            <i className="fa fa-spin fa-spinner" />
          </div>
        )}
      </div>
    </div>
  )
}

export default function OrganizationPage(props) {
  const { organization_id } = props.match.params
  const [token, setToken] = useState(localStorage[`token_${organization_id}`] || '')
  const [orgName, setOrgName] = useState('')
  const [widgetsEnabled, setWidgetsEnabled] = useState(false)
  const [orgLoadError, setOrgLoadError] = useState(false)
  const { t } = useTranslator()

  useEffect(() => {
    if (!token) return
    setOrgLoadError(false)
    tokenApi
      .get(token, '/dashboard/organization')
      .then(res => {
        if (res.data.name) setOrgName(res.data.name)
        setWidgetsEnabled(!!res.data.widgetsEnabled)
      })
      .catch(() => setOrgLoadError(true))
  }, [token])

  return (
    <Wrapper>
      {token ? (
        <React.Fragment>
          {orgName && <div className="org-name">{orgName}</div>}
          {orgLoadError && (
            <div className="passcode-error">
              {t('organizationLoadError') || 'Failed to load organization data — some sections may be missing.'}
            </div>
          )}
          <OrgNav organizationId={organization_id} widgetsEnabled={widgetsEnabled} t={t} />
          <Switch>
          {widgetsEnabled && (
          <Route
            path={`/yritys/${organization_id}/widget/:widget_id/:tab?`}
            render={() => <WidgetEdit token={token} organizationId={organization_id} t={t} />}
          />
          )}
          {widgetsEnabled && (
          <Route
            path={`/yritys/${organization_id}/widget`}
            render={() => <WidgetList token={token} organizationId={organization_id} t={t} />}
          />
          )}
          <Route
            render={() => (
              <ContentRoot token={token} organizationId={organization_id} routePrefix={props.match.url} t={t} />
            )}
          />
        </Switch>
        </React.Fragment>
      ) : (
        <PasscodeForm setToken={setToken} organizationId={organization_id} t={t} />
      )}
    </Wrapper>
  )
}
